<template>
  <div class="group-quorum d-flex align-center" data-test-id="group-quorum">
    <!-- Mixed groups (both approvers and signees) never support a quorum —
         show why instead of the controls below. -->
    <template v-if="mixedGroup">
      <div class="ml-auto">
        <v-icon aria-hidden="true" class="text-warning mr-2" size="16">
          fal fa-triangle-exclamation
        </v-icon>
        <span class="text-grey" data-test-id="quorum-mixed-group-warning">
          {{ t(mixedMessageKey) }}
        </span>
      </div>
    </template>

    <!-- No "Confirm" step: setting/editing the quorum applies immediately
         (see script), the SigningWorkflowDialog's own Confirm is what
         actually persists whatever is showing here. -->
    <template v-else>
      <v-spacer />
      <a
        v-if="quorum === null"
        class="deep-text-button d-inline-flex align-center group-quorum__set"
        data-test-id="quorum-set"
        @click="setInitialQuorum"
      >
        {{ t(allMustActKey, { count: total }) }}
        <v-icon aria-hidden="true" class="ml-2" color="primary" size="16"
          >fal fa-pen</v-icon
        >
      </a>

      <template v-else>
        <v-select
          v-model="draft"
          autofocus
          class="group-quorum__input"
          density="compact"
          hide-details
          :items="quorumOptions"
          variant="outlined"
        />
        <span class="group-quorum__of ml-2 mr-3">
          {{ t(ofCountMustActKey, { total }, draft) }}
        </span>
        <a
          class="deep-text-button"
          data-test-id="quorum-remove"
          @click="remove"
        >
          {{ t('buttons.remove') }}
        </a>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { PropType } from 'vue';

const props = defineProps({
  // Number of members in the group; the quorum is "N of total".
  total: {
    type: Number,
    required: true
  },
  // Current quorum (requiredSignatures / requiredApprovals), or null when none
  // is set (every member is required).
  quorum: {
    type: Number as PropType<number | null>,
    default: null
  },
  // True when the group mixes approvers and signees — the quorum feature
  // doesn't support that, so the component shows an explanatory message
  // instead of the controls.
  mixedGroup: {
    type: Boolean,
    default: false
  },
  // True when the group consists of approvers rather than signees — only the
  // wording differs ("must approve" instead of "must sign"). Irrelevant for
  // mixed groups, which never render these controls.
  approverGroup: {
    type: Boolean,
    default: false
  },
  // Rendered for the whole invitee list rather than for one sign-order group
  // (the sign order is off, so every invitee shares a single signee group).
  // Only switches the copy — "this group" and "mixed groups" mean nothing
  // when there are no groups on screen.
  ungrouped: {
    type: Boolean,
    default: false
  }
});

// Emitted on every applied change — Set/Edit (every edit applies live) or
// Remove. There is no separate "confirm" step anymore: whatever this control
// currently shows IS the group's quorum (still draft-only until the
// surrounding SigningWorkflowDialog's own Confirm persists it — see
// `setGroupQuorum`/`reconcileGroupQuorums`).
const emit = defineEmits(['change']);

const { t } = useI18n();

const draft = ref<number>(props.quorum ?? 1);

const allMustActKey = computed(() =>
  props.approverGroup
    ? 'texts.quorum_all_must_approve'
    : 'texts.quorum_all_must_sign'
);

const ofCountMustActKey = computed(() =>
  props.approverGroup
    ? 'texts.quorum_of_count_must_approve'
    : 'texts.quorum_of_count_must_sign'
);

// The quorum can never be below 1 nor exceed the group size, so the picker
// only ever offers that range. Explicit string titles keep the selected
// number rendering as text rather than relying on primitive-item coercion.
const quorumOptions = computed(() =>
  Array.from({ length: Math.max(props.total, 1) }, (_, index) => ({
    title: String(index + 1),
    value: index + 1
  }))
);
// Autofocus only the field the user just revealed by clicking "Set Quorum
// Requirement" — never one that renders with an already-configured quorum.
// The control mounts with every dialog open, and an unconditional autofocus
// there steals the caret from the invitee e-mail field the participants step
// focuses on mount.
const focusOnRender = ref(false);

const mixedMessageKey = computed(() =>
  props.ungrouped
    ? 'texts.quorum_approvers_not_supported'
    : 'texts.quorum_mixed_group_not_supported'
);

// Mirrors an externally changed quorum (e.g. clamped elsewhere to the
// group's shrunk membership) back into the field. A no-op when the change
// originated from this component's own edits below, since `draft` already
// holds that value.
watch(
  () => props.quorum,
  (value) => {
    if (value !== null) draft.value = value;
  }
);

// The quorum can never exceed the group size, so cap the selection live
// (covers the group shrinking while the picker is open).
watch([draft, () => props.total], () => {
  if (typeof draft.value === 'number' && draft.value > props.total) {
    draft.value = props.total;
  }
});

// Every change to `draft` — picking an option or the clamp above — applies
// immediately, since there is no Confirm button left to gate it on.
watch(draft, (value) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return;
  emit('change', clamp(value));
});

function clamp(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(Math.max(Math.round(value), 1), props.total);
}

// Clicking the "all must sign" label applies a quorum right away (the
// minimum, 1) — the picker and "Remove" affordance appear already live, with
// no separate Confirm step to click afterwards.
function setInitialQuorum() {
  focusOnRender.value = true;
  draft.value = 1;
  emit('change', clamp(draft.value));
}

function remove() {
  focusOnRender.value = false;
  emit('change', null);
}
</script>

<style scoped lang="scss">
.group-quorum {
  min-height: 48px;
}

.group-quorum__set {
  color: rgb(var(--v-theme-grey-darken-4));
  &:hover {
    color: rgb(var(--v-theme-grey-darken-4));
  }
}

.group-quorum__input {
  flex: 0 0 auto;
  min-width: 76px;
}

// Vuetify lays the field out as a grid whose text column is `minmax(0, 1fr)`,
// so constraining the field's width collapses that column and hides the
// selected number (`.v-select__selection-text` is `overflow: hidden`) while
// the chevron — a `min-content` track — keeps its size. Let the field size to
// its content and trim the padding a 1-2 digit value doesn't need instead.
.group-quorum__input :deep(.v-field__input) {
  padding-inline: 12px 0;
}

.group-quorum__input :deep(.v-field__append-inner) {
  padding-inline-start: 0;
}

.group-quorum__of {
  white-space: nowrap;
}
</style>
